import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { HiChevronRight, HiOutlineHome } from "react-icons/hi";

// @ts-ignore — untyped JS store of the in-memory geography selection.
import { getOneCountry, getOneLevel, getOneRegion } from "../lib/breadCrumb/BreadCrumb";
import {
  getCityDashboard,
  getCurrentRegion,
  getSingleCountryDashboard,
  getSingleLevelDashboard,
// @ts-ignore — untyped JS navigation helpers.
} from "../lib/sysNavigation/SysNavigation";

type CrumbSpec = {
  /** Human label rendered in the breadcrumb. */
  label: string;
  /** Route to navigate to when this crumb is clicked. Defaults to the matched path. */
  route?: string;
};

/**
 * Every known path prefix gets a human label. The breadcrumb walks the current
 * pathname and emits one crumb per matched prefix — anything unknown (ids,
 * unreachable segments) is silently skipped.
 *
 * Keep this in sync with `src/app/routing/routes.ts`.
 */
const PREFIX_CRUMBS: Record<string, CrumbSpec> = {
  // ─── Geography ────────────────────────────────────────────────
  "/region": { label: "Regions", route: "/region/manage" },
  "/region/add": { label: "Add Region" },
  "/region/edit": { label: "Edit Region" },
  "/region/dashboard": { label: "Dashboard" },

  "/countries": { label: "Countries" },
  "/countries/add": { label: "Add Country" },
  "/countries/dashboard": { label: "Dashboard" },
  "/countries/edit": { label: "Edit Country" },
  "/country/dashboard": { label: "Dashboard" },

  "/levels": { label: "Levels", route: "/levels/first" },
  "/levels/first": { label: "First Level" },
  "/levels/second": { label: "Second Level" },
  "/levels/third": { label: "Third Level" },
  "/levels/fourth": { label: "Fourth Level" },
  "/levels/dashboard": { label: "Dashboard" },
  "/level/add": { label: "Add Level" },
  "/level/edit": { label: "Edit Level" },
  "/level/dashboard": { label: "Dashboard" },

  "/cities": { label: "Cities" },
  "/cities/dashboard": { label: "Dashboard" },
  "/city/dashboard": { label: "Dashboard" },

  // ─── DoSee content ────────────────────────────────────────────
  "/dosee/data": { label: "Content" },

  "/data/event": { label: "Events" },
  "/data/event/add": { label: "New" },
  "/data/event/edit": { label: "Edit" },

  "/data/attraction": { label: "Attractions" },
  "/data/attraction/add": { label: "New" },
  "/data/attraction/edit": { label: "Edit" },
  "/data/attraction/view": { label: "View" },

  "/data/bestofafrica": { label: "Best of Africa" },
  "/data/bestofafrica/add": { label: "New" },
  "/data/bestofafrica/edit": { label: "Edit" },
  "/data/bestofafrica/view": { label: "View" },

  "/data/cinema": { label: "Cinemas" },
  "/data/cinema/add": { label: "New" },
  "/data/cinema/edit": { label: "Edit" },
  "/data/cinema/view": { label: "View" },

  "/data/music": { label: "Music" },
  "/data/music/add": { label: "New" },
  "/data/music/edit": { label: "Edit" },
  "/data/music/view": { label: "View" },

  "/data/news": { label: "News" },
  "/data/news/add": { label: "New" },
  "/data/news/edit": { label: "Edit" },
  "/data/news/view": { label: "View" },

  "/data/safari": { label: "Safaris" },
  "/data/safari/add": { label: "New" },
  "/data/safari/edit": { label: "Edit" },
  "/data/safari/view": { label: "View" },

  "/data/restaurant": { label: "Restaurants" },
  "/data/restaurant/add": { label: "New" },
  "/data/restaurant/edit": { label: "Edit" },
  "/data/restaurant/view": { label: "View" },

  "/data/things/todo": { label: "Things To Do" },
  "/data/things/todo/add": { label: "New" },
  "/data/things/todo/edit": { label: "Edit" },

  "/data/party": { label: "Parties" },
  "/data/party/add": { label: "New" },
  "/data/party/edit": { label: "Edit" },

  "/data/nightlife": { label: "Nightlife" },
  "/data/nightlife/add": { label: "New" },
  "/data/nightlife/edit": { label: "Edit" },

  "/data/city/guide": { label: "City Guides" },
  "/data/city/guide/add": { label: "New" },
  "/data/city/guide/edit": { label: "Edit" },

  "/data/hotel": { label: "Hotels" },
  "/data/hotel/add": { label: "New" },
  "/data/hotel/edit": { label: "Edit" },

  "/data/class": { label: "Classes" },
  "/data/class/add": { label: "New" },
  "/data/class/edit": { label: "Edit" },

  "/data/shopping": { label: "Shopping" },
  "/data/shopping/add": { label: "New" },
  "/data/shopping/edit": { label: "Edit" },

  "/data/food": { label: "Food" },
  "/data/food/add": { label: "New" },
  "/data/food/edit": { label: "Edit" },

  "/data/book": { label: "Books" },
  "/data/book/add": { label: "New" },
  "/data/book/edit": { label: "Edit" },

  "/data/neighborhood": { label: "Neighborhoods" },
  "/data/neighborhood/add": { label: "New" },
  "/data/neighborhood/edit": { label: "Edit" },

  "/data/ministry": { label: "Ministries" },
  "/data/ministry/add": { label: "New" },
  "/data/ministry/edit": { label: "Edit" },

  "/data/get/around": { label: "Getting Around" },
  "/data/get/around/add": { label: "New" },
  "/data/get/around/edit": { label: "Edit" },

  "/data/art": { label: "Art" },
  "/data/art/add": { label: "New" },
  "/data/art/edit": { label: "Edit" },

  "/data/movie": { label: "Movies" },
  "/data/movie/add": { label: "New" },
  "/data/movie/edit": { label: "Edit" },

  "/data/dance": { label: "Dance" },
  "/data/dance/add": { label: "New" },
  "/data/dance/edit": { label: "Edit" },

  "/data/theater": { label: "Theaters" },
  "/data/theater/add": { label: "New" },
  "/data/theater/edit": { label: "Edit" },

  "/data/tips/advice": { label: "Tips & Advice" },
  "/data/tips/advice/add": { label: "New" },
  "/data/tips/advice/edit": { label: "Edit" },

  "/data/museum": { label: "Museums" },
  "/data/museum/add": { label: "New" },
  "/data/museum/edit": { label: "Edit" },

  "/data/tour/package": { label: "Tour Packages" },
  "/data/tour/package/add": { label: "New" },
  "/data/tour/package/edit": { label: "Edit" },

  "/data/product": { label: "Products" },
  "/data/product/add": { label: "New" },
  "/data/product/edit": { label: "Edit" },

  "/data/company": { label: "Companies" },
  "/data/company/add": { label: "New" },
  "/data/company/edit": { label: "Edit" },

  "/data/embassy": { label: "Embassies" },
  "/data/embassy/add": { label: "New" },
  "/data/embassy/edit": { label: "Edit" },

  "/data/advertisement": { label: "Advertisements" },
  "/data/advertisement/add": { label: "New" },
  "/data/advertisement/edit": { label: "Edit" },

  // ─── People ───────────────────────────────────────────────────
  "/team": { label: "Team" },
  "/team/add": { label: "Add" },
  "/team/edit": { label: "Edit" },

  "/agent": { label: "Agents" },
  "/agent/add": { label: "Add Agent" },
  "/agent/edit": { label: "Edit Agent" },

  "/admin": { label: "Admins" },
  "/admin/add": { label: "Add Admin" },
  "/admin/edit": { label: "Edit Admin" },

  "/user": { label: "Users" },
  "/user/add": { label: "Add User" },
  "/user/edit": { label: "Edit User" },

  // ─── Reference data ──────────────────────────────────────────
  "/trade/org": { label: "Trade Organizations" },
  "/trade/org/add": { label: "New" },
  "/trade/org/edit": { label: "Edit" },

  "/regional/blocks": { label: "Regional Blocks" },
  "/regional/blocks/add": { label: "New" },
  "/regional/blocks/edit": { label: "Edit" },

  "/plug/image": { label: "Plug Images" },
  "/plug/image/add": { label: "New" },
  "/plug/image/edit": { label: "Edit" },

  "/climate": { label: "Climate" },
  "/climate/add": { label: "New" },
  "/climate/edit": { label: "Edit" },

  "/ethnic/group": { label: "Ethnic Groups" },
  "/ethnic/group/add": { label: "New" },
  "/ethnic/group/edit": { label: "Edit" },

  "/language": { label: "Languages" },
  "/language/add": { label: "New" },
  "/language/edit": { label: "Edit" },

  "/religion": { label: "Religions" },
  "/religion/add": { label: "New" },
  "/religion/edit": { label: "Edit" },

  // ─── Site content ────────────────────────────────────────────
  "/banner": { label: "Banners" },
  "/banner/add": { label: "New" },
  "/banner/edit": { label: "Edit" },

  "/page": { label: "Pages" },
  "/page/add": { label: "Add" },
  "/page/edit": { label: "Edit" },

  "/blog": { label: "Blog" },
  "/blog/add": { label: "New post" },
  "/blog/edit": { label: "Edit" },

  "/newsletter": { label: "Newsletter" },

  "/footer": { label: "Footer" },
  "/footer/edit": { label: "Edit" },

  "/ai_chat_info": { label: "AI Chat Info" },
  "/ai_chat_info/edit": { label: "Edit" },

  "/terms": { label: "Terms & Conditions" },
  "/terms/edit": { label: "Edit" },

  "/privacy": { label: "Privacy Policy" },
  "/privacy/edit": { label: "Edit" },

  "/service": { label: "Services" },
  "/service/edit": { label: "Edit" },

  "/contact": { label: "Contact" },

  "/about": { label: "About" },
  "/about/edit": { label: "Edit" },

  "/email": { label: "Email Templates" },
  "/email/edit": { label: "Edit" },

  "/partner": { label: "Partners" },
  "/partner/add": { label: "New" },
  "/partner/edit": { label: "Edit" },

  // ─── Company ─────────────────────────────────────────────────
  "/company": { label: "Companies" },
  "/company/add": { label: "New" },
  "/company/edit": { label: "Edit" },
  "/company/view": { label: "View" },
  "/company/dashboard": { label: "Dashboard" },

  // ─── Commerce ────────────────────────────────────────────────
  "/payment": { label: "Payments" },
  "/currency": { label: "Currencies" },
  "/currency/add": { label: "New" },
  "/currency/edit": { label: "Edit" },

  // ─── Misc ────────────────────────────────────────────────────
  "/search": { label: "Search" },
  "/password": { label: "My Password" },
  "/content/stats": { label: "Content Stats" },
  "/content/admin/stats": { label: "Admin Stats" },
  "/generated": { label: "Generated Content" },
  "/chat_support": { label: "Chat Support" },
};

const HOME_ROUTE = "/";
const NUMERIC_RE = /^\d+$/;
const SCOPED_RE = /^\/(countries|levels|cities|dosee|data)(\/|$)/;

type Crumb = { label: string; route: string };

const prefixCrumbs = (pathname: string): Crumb[] => {
  const raw = pathname.split("/").filter(Boolean);
  const segments = raw.filter((s) => !NUMERIC_RE.test(s));

  const out: Crumb[] = [];
  let acc = "";
  for (const seg of segments) {
    acc += `/${seg}`;
    const spec = PREFIX_CRUMBS[acc];
    if (spec) out.push({ label: spec.label, route: spec.route ?? acc });
  }
  return out;
};

/**
 * Reads the in-memory geography selection stored by the dashboard navigation
 * helpers. These stores are plain module-scoped arrays — they survive across
 * in-session navigations but are cleared on a hard reload. That's fine; when
 * the store is empty we simply fall through to the path-derived breadcrumb.
 */
const ancestorCrumbs = ({
  includeLevel = false,
}: { includeLevel?: boolean } = {}): Crumb[] => {
  const ancestors: Crumb[] = [];
  const region = (getOneRegion() as any[])?.[0];
  const country = (getOneCountry() as any[])?.[0];
  if (region?.name) {
    ancestors.push({
      label: region.name,
      route: region.route ?? "/region/manage",
    });
  }
  if (country?.name) {
    ancestors.push({
      label: country.name,
      route: country.route ?? "/countries",
    });
  }
  if (includeLevel) {
    const level = (getOneLevel() as any[])?.[0];
    if (level?.name) {
      ancestors.push({
        label: level.name,
        route: level.route ?? "/levels/first",
      });
    }
  }
  return ancestors;
};

const buildCrumbs = (pathname: string): Crumb[] => {
  // ── Region dashboard ─────────────────────────────────────────
  if (pathname.startsWith("/region/dashboard")) {
    const name = (getCurrentRegion() as any[])?.[0]?.name;
    return [
      { label: "Regions", route: "/region/manage" },
      { label: name ?? "Dashboard", route: pathname },
    ];
  }

  // ── Country dashboard ────────────────────────────────────────
  if (
    pathname.startsWith("/country/dashboard") ||
    pathname.startsWith("/countries/dashboard")
  ) {
    const storeName = getSingleCountryDashboard();
    const country = (getOneCountry() as any[])?.[0];
    const region = (getOneRegion() as any[])?.[0];
    const name =
      (typeof storeName === "string" && storeName !== "Country"
        ? storeName
        : null) ??
      country?.name ??
      "Dashboard";

    return [
      ...(region?.name
        ? [{ label: region.name, route: region.route ?? "/region/manage" }]
        : []),
      { label: "Countries", route: "/countries" },
      { label: name, route: pathname },
    ];
  }

  // ── City dashboard ───────────────────────────────────────────
  if (
    pathname.startsWith("/city/dashboard") ||
    pathname.startsWith("/cities/dashboard")
  ) {
    const dash = getCityDashboard();
    const city = Array.isArray(dash) ? dash[1] : null;
    const cityName =
      (city && typeof city === "object" && (city as any).name) || null;
    return [
      ...ancestorCrumbs(),
      { label: "Cities", route: "/cities" },
      { label: cityName ?? "Dashboard", route: pathname },
    ];
  }

  // ── Level dashboard ──────────────────────────────────────────
  if (
    pathname.startsWith("/level/dashboard") ||
    pathname.startsWith("/levels/dashboard")
  ) {
    const dash = getSingleLevelDashboard();
    const lvlName =
      dash && typeof dash === "object" ? (dash as any).data?.name : null;
    return [
      ...ancestorCrumbs(),
      { label: "Levels", route: "/levels/first" },
      { label: lvlName ?? "Dashboard", route: pathname },
    ];
  }

  // ── Default: path-driven, with ancestor enrichment for geo pages ──
  const basic = prefixCrumbs(pathname);
  if (SCOPED_RE.test(pathname)) {
    // On content pages the selected level (city) is meaningful context — include it.
    const onContent = /^\/(dosee\/data|data\/)/.test(pathname);
    const ancestors = ancestorCrumbs({ includeLevel: onContent });
    // Avoid duplicating any basic crumb whose route already appears in the ancestor trail.
    const filteredBasic = basic.filter(
      (c) => !ancestors.some((a) => a.route === c.route),
    );
    return [...ancestors, ...filteredBasic];
  }
  return basic;
};

type Props = {
  /**
   * Kept for backwards compatibility — the older breadcrumb used it to reset
   * an in-memory geography store. The path-driven breadcrumb doesn't need it,
   * but call sites still pass it so we keep the prop accepted as a no-op.
   */
  default_value?: boolean;
};

export const BreadCrumbComponent: React.FC<Props> = () => {
  const { pathname } = useLocation();
  const crumbs = useMemo(() => buildCrumbs(pathname), [pathname]);

  // On the bare home route there's nothing meaningful to show.
  if (pathname === HOME_ROUTE && crumbs.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="w-full overflow-x-auto">
      <ol className="inline-flex max-w-full items-center gap-1 rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 shadow-sm">
        <li className="flex items-center">
          <CrumbLink
            route={HOME_ROUTE}
            label="Home"
            icon={<HiOutlineHome className="h-4 w-4" />}
            isLast={crumbs.length === 0}
          />
          {crumbs.length > 0 ? <Separator /> : null}
        </li>

        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <li key={`${crumb.route}-${idx}`} className="flex items-center">
              <CrumbLink
                route={crumb.route}
                label={crumb.label}
                isLast={isLast}
              />
              {!isLast ? <Separator /> : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

const Separator: React.FC = () => (
  <span aria-hidden className="mx-0.5 text-gray-300">
    <HiChevronRight className="h-3.5 w-3.5" />
  </span>
);

type CrumbLinkProps = {
  route: string;
  label: string;
  icon?: React.ReactNode;
  isLast: boolean;
};

const CrumbLink: React.FC<CrumbLinkProps> = ({ route, label, icon, isLast }) => {
  const base =
    "group inline-flex max-w-[220px] items-center gap-1.5 rounded-lg px-2 py-1 text-[12.5px] transition-colors";
  const active =
    "text-gray-900 font-semibold bg-brand/5 ring-1 ring-inset ring-brand/15";
  const idle = "text-gray-600 hover:bg-gray-50 hover:text-gray-900";

  const content = (
    <>
      {icon ? (
        <span
          className={
            isLast ? "shrink-0 text-brand" : "shrink-0 text-gray-400 group-hover:text-gray-600"
          }
        >
          {icon}
        </span>
      ) : null}
      <span className="truncate">{label}</span>
    </>
  );

  if (isLast) {
    return (
      <span aria-current="page" className={`${base} ${active}`} title={label}>
        {content}
      </span>
    );
  }

  return (
    <Link to={route} className={`${base} ${idle}`} title={label}>
      {content}
    </Link>
  );
};
